/**
 * AddExistingPhotosModal - Multi-select modal for adding existing photos to a collection
 *
 * Displays all user's photos in a PhotoCollection2 grid with multi-select enabled.
 * Users can select multiple photos and add them to the current collection.
 *
 * @module AddExistingPhotosModal
 */

import { useState, useMemo } from 'react';
import { Modal } from '@/components/composed/Modal';
import { PhotoCollection2 } from '@/components/PhotoCollection2';
import { Button } from '@/components/atoms';
import { LoadingState, ErrorState } from '@/components/composed';
import { usePhotosGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import type { PhotoItem2ViewModel } from '@/components/PhotoItem2/PhotoItem2.types';

export interface AddExistingPhotosModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Callback when photos are selected and "Add" is clicked
   * Receives array of selected photo global IDs
   */
  onAdd: (photoIds: string[]) => void;

  /**
   * Optional: IDs of photos already in the collection to exclude from selection
   */
  existingPhotoIds?: string[];
}

export function AddExistingPhotosModal({
  open,
  onClose,
  onAdd,
  existingPhotoIds = [],
}: AddExistingPhotosModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Fetch all user's photos using GraphQL
  // Note: usePhotosGraphQL fetches for the current authenticated user
  // Using a large limit to get all photos for the picker
  const { photos, loading: isLoading, error } = usePhotosGraphQL(1000);

  // Convert Photo[] to PhotoItem2ViewModel[] and filter out photos already in collection
  const photoItems = useMemo<PhotoItem2ViewModel[]>(() => {
    if (!photos) return [];

    return photos
      .filter(photo => !existingPhotoIds.includes(photo.globalId || photo.id))
      .map(photo => ({
        photoData: photo,
        critiqueCount: 0,
        textContent: undefined,
        order: null,
      }));
  }, [photos, existingPhotoIds]);

  const handleAdd = () => {
    if (selectedIds.length === 0) return;
    onAdd(selectedIds);
    setSelectedIds([]);
  };

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end">
      <Button variant="ghost" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleAdd}
        disabled={selectedIds.length === 0}
      >
        Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Existing Photos"
      size="full"
      footer={footer}
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-[60vh] min-h-0">
        {isLoading ? (
          <LoadingState message="Loading photos..." size="md" />
        ) : error ? (
          <ErrorState
            title="Failed to load photos"
            message={error.message || 'Could not load your photos'}
          />
        ) : photoItems.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted">
              {existingPhotoIds.length > 0
                ? 'All your photos are already in this collection.'
                : 'You don\'t have any photos yet. Upload some photos first.'}
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-hidden">
            <PhotoCollection2
              photos={photoItems}
              layout="grid"
              itemTemplate="photo-only"
              selectionMode="multiple"
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              zoom={1}
              className="w-full h-full"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
